import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Pencil,
  ShieldCheck,
  Infinity as InfinityIcon,
  Sparkles,
  HeartHandshake,
  Check,
  X,
} from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import StickyCTA from "@/components/StickyCTA";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import useSeo from "@/hooks/useSeo";
import { getRefCode } from "@/lib/referral";

const TMI_JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Course",
      "@id": "https://pipbennett.com/the-menopause-invitation#course",
      "name": "The Menopause Invitation",
      "description": "A six-module digital coaching program for people navigating menopause who want to meet it with awareness, not anxiety. Includes an interactive auto-saving workbook.",
      "provider": { "@id": "https://pipbennett.com/#organization" },
      "url": "https://pipbennett.com/the-menopause-invitation",
      "inLanguage": "en-AU",
      "offers": {
        "@type": "Offer",
        "price": "87.00",
        "priceCurrency": "AUD",
        "availability": "https://schema.org/InStock",
        "url": "https://pipbennett.com/the-menopause-invitation"
      },
      "hasCourseInstance": {
        "@type": "CourseInstance",
        "courseMode": "Online",
        "courseWorkload": "PT4H"
      }
    },
    {
      "@type": "FAQPage",
      "@id": "https://pipbennett.com/the-menopause-invitation#faq",
      "url": "https://pipbennett.com/the-menopause-invitation",
      "mainEntity": [
        {
          "@type": "Question",
          "@id": "https://pipbennett.com/the-menopause-invitation#faq-medical",
          "name": "Is The Menopause Invitation medical advice?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. The Menopause Invitation is a coaching program, not medical care. It sits alongside the work of medical professionals and does not replace personalised medical advice. If you need medical guidance about menopause symptoms or treatment, please speak with your GP or a menopause-specialist clinician."
          }
        },
        {
          "@type": "Question",
          "@id": "https://pipbennett.com/the-menopause-invitation#faq-therapy",
          "name": "Is The Menopause Invitation therapy?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. This is forward-facing coaching, which is awareness-building work, not therapy. If tender material surfaces during the program, it should be brought to a qualified therapist or counsellor for proper support."
          }
        },
        {
          "@type": "Question",
          "@id": "https://pipbennett.com/the-menopause-invitation#faq-workbook",
          "name": "How does the reflection workbook work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Each module ends with a small set of reflection questions. Your answers are saved privately to your account in real time as you type, so you can return whenever you would like to add more or notice how your perspective has shifted across the program."
          }
        },
        {
          "@type": "Question",
          "@id": "https://pipbennett.com/the-menopause-invitation#faq-duration",
          "name": "How long does The Menopause Invitation take to complete?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most people move through the six modules across three to six weeks at their own pace. You have lifetime access to the program, so there is no race and no expiry date. Return to the modules and your reflections whenever you would like to."
          }
        }
      ]
    }
  ]
};

const PIP_IMAGE = "/tmi-poster-v4.png";
const TMI_VIDEO =
  "https://customer-assets.emergentagent.com/job_pip-revamp/artifacts/65gnaonw_06973555-AC2E-4282-A0B6-0E58920CE855.mp4";

const MODULES = [
  {
    n: "01",
    title: "Reframing Menopause",
    blurb:
      "From something happening to you → to something you can understand and meet consciously.",
  },
  {
    n: "02",
    title: "What if Your Biology Isn't Betraying You?",
    blurb: "From 'my body is the problem' → to 'my body is communicating'.",
  },
  {
    n: "03",
    title: "The Identity Threshold",
    blurb: "From 'I've lost myself' → to 'I am being invited to redefine myself'.",
  },
  {
    n: "04",
    title: "Energy, Health & The Body as Foundation",
    blurb: "From reactive health → to intentional self-leadership.",
  },
  {
    n: "05",
    title: "Designing Your Life",
    blurb: "From default living → to conscious, self-led choice.",
  },
  {
    n: "06",
    title: "The Invitation Fully Accepted",
    blurb: "From understanding → to embodied self-leadership.",
  },
];

const INCLUDED = [
  { icon: BookOpen, label: "Six considered teaching modules + a welcome orientation" },
  { icon: Pencil, label: "An interactive workbook that auto-saves to your private account" },
  { icon: HeartHandshake, label: "A clear coaching frame — grounded, honest, and respectful of medical care" },
  { icon: InfinityIcon, label: "Lifetime access. Yours to revisit as your understanding deepens" },
  { icon: ShieldCheck, label: "14-day peace-of-mind guarantee" },
];

const FAQ = [
  {
    q: "Is this medical advice?",
    a: "No — and it's important to be clear about that. The Menopause Invitation is a coaching program, not medical care. It is designed to sit alongside the work of medical professionals like Dr Jen Gunter and Dr Mary Claire Haver. If you are experiencing symptoms that need clinical support, please seek personalised medical advice. This space complements that — it doesn't replace it.",
  },
  {
    q: "Is this therapy?",
    a: "No. This is coaching — forward-facing, awareness-building work. If something tender or unresolved surfaces (which is often natural in this phase of life), I encourage you to bring that to a qualified therapist or counsellor. Knowing what kind of support you need is part of leading yourself well.",
  },
  {
    q: "How does the workbook work?",
    a: "Each module ends with a small set of reflection questions. Your answers are saved privately to your account in real-time as you type — so you can return whenever you'd like to add more, re-read what you wrote, or notice how your perspective has shifted over time.",
  },
  {
    q: "How long does it take?",
    a: "The modules are designed to be moved through at your own pace — most people take 3–6 weeks. You have lifetime access. There is no race here. Allow each module to settle before moving to the next.",
  },
  {
    q: "Who is this not for?",
    a: "If you're looking for symptom management protocols, hormone advice, or a quick fix — this is not that program. The Menopause Invitation is a quieter, deeper conversation about how you understand and meet this transition. It works best if you're willing to sit with questions rather than rush to answers.",
  },
  {
    q: "Refunds and questions?",
    a: "There is a 14-day peace-of-mind guarantee on every purchase. If The Menopause Invitation isn't the right fit for you within the first two weeks, simply email pip@pipbennett.com and Pip will refund you in full — no questions, no awkwardness. For any other questions before you buy, the same email works: pip@pipbennett.com.",
  },
];

/**
 * Dual-pathway card with a soft "More details ↔ Back" flip.
 * Front: title, price, short summary, primary CTA, "More details" link.
 * Back:  the longer "for you if" description + "Back" link.
 * Heights are matched via min-h so both cards align side by side.
 */
function PathwayCard({
  side,
  isFlipped,
  onFlip,
  title,
  price,
  summary,
  details,
  buttonLabel,
  onChoose,
  loading,
  testIdPrefix,
}) {
  const accentBorder =
    side === "research" ? "border-gold/40 hover:border-gold" : "border-sage/30 hover:border-sage";
  const priceColour = side === "research" ? "text-gold-dark" : "text-sage";
  return (
    <div
      className={`relative rounded-[1.5rem] border-2 ${accentBorder} bg-cream p-7 md:p-9 flex flex-col min-h-[26rem] transition-colors`}
      data-testid={testIdPrefix}
    >
      {!isFlipped ? (
        <>
          {side === "research" && (
            <div className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.18em] uppercase font-semibold text-gold-dark mb-3">
              Co-creator price · Co-Creators Circle
            </div>
          )}
          <h4 className="font-serif-display text-2xl md:text-3xl text-ink leading-snug">
            {title}
          </h4>
          <div className={`mt-3 font-serif-display text-3xl md:text-4xl ${priceColour}`}>
            {price}
          </div>
          <p className="mt-5 text-ink-soft leading-relaxed text-[0.97rem] flex-1">
            {summary}
          </p>
          <div className="mt-7 flex flex-col gap-3">
            <button
              type="button"
              onClick={onChoose}
              disabled={loading}
              data-testid={`${testIdPrefix}-cta`}
              className={`inline-flex items-center justify-center gap-2 ${
                side === "research"
                  ? "bg-gold hover:bg-gold-dark text-ink"
                  : "bg-sage hover:bg-sage-dark text-cream"
              } disabled:opacity-50 rounded-full px-6 py-3.5 font-medium tracking-wide text-sm md:text-base transition-all`}
            >
              {buttonLabel}
              <ArrowRight size={16} strokeWidth={1.8} />
            </button>
            <button
              type="button"
              onClick={onFlip}
              className="text-xs font-medium text-ink-soft hover:text-ink underline underline-offset-4 transition self-center"
              data-testid={`${testIdPrefix}-details`}
            >
              More details about this pathway
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="eyebrow text-ink-soft mb-3">About this pathway</div>
          <h4 className="font-serif-display text-xl md:text-2xl text-ink leading-snug mb-5">
            {title} — {price}
          </h4>
          <div className="text-ink-soft leading-relaxed text-[0.95rem] space-y-3 flex-1">
            {details.map((d, i) => (
              <p key={i}>{d}</p>
            ))}
          </div>
          <div className="mt-7 flex flex-col gap-3">
            <button
              type="button"
              onClick={onChoose}
              disabled={loading}
              data-testid={`${testIdPrefix}-cta-back`}
              className={`inline-flex items-center justify-center gap-2 ${
                side === "research"
                  ? "bg-gold hover:bg-gold-dark text-ink"
                  : "bg-sage hover:bg-sage-dark text-cream"
              } disabled:opacity-50 rounded-full px-6 py-3.5 font-medium tracking-wide text-sm md:text-base transition-all`}
            >
              {buttonLabel}
              <ArrowRight size={16} strokeWidth={1.8} />
            </button>
            <button
              type="button"
              onClick={onFlip}
              className="text-xs font-medium text-ink-soft hover:text-ink underline underline-offset-4 transition self-center"
            >
              ← Back to overview
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function TheMenopauseInvitation() {
  useSeo({
    title: "The Menopause Invitation — Pip Bennett ($87 AUD)",
    description: "A six-module digital coaching program for the person moving through menopause who wants to meet it with awareness, not anxiety. Interactive workbook included.",
    canonical: "https://pipbennett.com/the-menopause-invitation",
    jsonLd: TMI_JSONLD,
  });
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [agreementBox1, setAgreementBox1] = useState(false);
  const [agreementBox2, setAgreementBox2] = useState(false);
  // Which pathway is currently in checkout. Default is the standard $87 digital.
  const [selectedPackage, setSelectedPackage] = useState("menopause_invitation");

  const proceedToCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: selectedPackage,
        origin_url: window.location.origin,
        agreement_accepted: true,
        ref_code: getRefCode() || undefined,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  const handleBuy = (packageId = "menopause_invitation") => {
    setSelectedPackage(packageId);
    setAgreementBox1(false);
    setAgreementBox2(false);
    setAgreementOpen(true);
  };

  const alreadyOwns = user && user !== false && user.has_menopause_invitation_access;
  const canProceed = agreementBox1 && agreementBox2 && !loading;

  return (
    <div data-testid="page-tmi">
      {/* AGREEMENT MODAL */}
      {agreementOpen && (
        <div
          className="fixed inset-0 z-[80] bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          data-testid="tmi-agreement-modal"
          onClick={() => !loading && setAgreementOpen(false)}
        >
          <div
            className="bg-cream-soft border border-hairline rounded-2xl max-w-xl w-full p-8 md:p-10 my-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => !loading && setAgreementOpen(false)}
              className="absolute top-5 right-5 text-ink-soft hover:text-ink"
              aria-label="Close"
              data-testid="tmi-agreement-close"
            >
              <X size={20} strokeWidth={1.5} />
            </button>
            <div className="eyebrow mb-3 text-sage flex items-center gap-2">
              <ShieldCheck size={12} strokeWidth={1.8} /> Before You Begin
            </div>
            <h3 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight mb-5">
              A coaching agreement, in plain words.
            </h3>
            <div className="space-y-4 text-ink-soft leading-relaxed text-[0.95rem] mb-7">
              <p>
                The Menopause Invitation is a <strong className="text-sage">coaching program</strong>.
                It is not medical advice, diagnosis, or treatment, and it is not therapy. It sits
                alongside — never in place of — the work of qualified medical and mental health
                professionals.
              </p>
              <p>
                If you're navigating significant symptoms, please speak with your GP or a menopause
                specialist. If something tender surfaces as you move through this work, please reach
                out to a qualified therapist. Self-leadership includes knowing what kind of support
                you need.
              </p>
            </div>

            <label className="flex items-start gap-3 mb-4 cursor-pointer p-4 bg-white border border-hairline rounded-xl hover:border-sage transition" data-testid="tmi-agreement-box-1">
              <input
                type="checkbox"
                checked={agreementBox1}
                onChange={(e) => setAgreementBox1(e.target.checked)}
                className="mt-1 w-5 h-5 accent-sage flex-shrink-0"
              />
              <span className="text-sm text-ink leading-relaxed">
                I understand The Menopause Invitation is a <strong>coaching program — not therapy
                or medical treatment</strong>. I will continue to seek personalised medical advice
                where appropriate.
              </span>
            </label>

            <label className="flex items-start gap-3 mb-7 cursor-pointer p-4 bg-white border border-hairline rounded-xl hover:border-sage transition" data-testid="tmi-agreement-box-2">
              <input
                type="checkbox"
                checked={agreementBox2}
                onChange={(e) => setAgreementBox2(e.target.checked)}
                className="mt-1 w-5 h-5 accent-sage flex-shrink-0"
              />
              <span className="text-sm text-ink leading-relaxed">
                I take responsibility for my own wellbeing, and I will reach out to a qualified
                professional if anything ever feels overwhelming.
              </span>
            </label>

            <button
              onClick={proceedToCheckout}
              disabled={!canProceed}
              className="w-full inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="tmi-agreement-continue"
            >
              {loading
                ? "Taking you to checkout…"
                : selectedPackage === "menopause_invitation_research"
                ? "I agree — continue to Co-Creators checkout ($40)"
                : "I agree — continue to checkout ($87)"}
              {!loading && <ArrowRight size={16} strokeWidth={1.8} />}
            </button>
            <p className="mt-4 text-xs text-ink-soft text-center">
              Secure payment via Stripe · 14-day peace-of-mind guarantee
            </p>
          </div>
        </div>
      )}

      {/* HERO */}
      <section className="relative pt-12 md:pt-20 pb-24 md:pb-32 overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{ background: "linear-gradient(135deg, #F1E0D0 0%, #F5EDE4 60%, #E3EBE4 100%)" }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <SectionReveal className="lg:col-span-7">
            <div className="eyebrow mb-6 text-gold-dark">Digital Coaching Program · $87 AUD</div>
            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl text-ink leading-[1.02]">
              The <em className="italic text-sage">Menopause</em> Invitation.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-ink leading-relaxed max-w-xl">
              You've done the research. You know about HRT, phytoestrogens and pelvic floors. This isn't that.
            </p>
            <p className="mt-5 text-base md:text-lg text-ink-soft leading-relaxed max-w-xl">
              The Menopause Invitation is a six-module coaching experience for the person who is done
              treating menopause as a problem to manage — and ready to meet it as the invitation it
              actually is. Not a symptom-fixer. Not a hormone protocol. A different lens — and a
              quieter, more honest conversation with yourself.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              {alreadyOwns ? (
                <Link
                  to="/dashboard/menopause"
                  className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all"
                  data-testid="tmi-dashboard-cta"
                >
                  Continue your work
                  <ArrowRight size={16} strokeWidth={1.8} />
                </Link>
              ) : (
                <a
                  href="#tmi-qualifier"
                  className="inline-flex items-center gap-2 text-sage hover:text-sage-dark font-medium underline-offset-4 hover:underline transition"
                  data-testid="tmi-hero-explore-link"
                >
                  Is this for you?
                  <ArrowRight size={14} strokeWidth={1.8} />
                </a>
              )}
            </div>
          </SectionReveal>
          <SectionReveal delay={150} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-8 bg-peach/40 rounded-[2rem] blur-2xl -z-10" />
              <video
                src={TMI_VIDEO}
                poster={PIP_IMAGE}
                controls
                playsInline
                preload="metadata"
                className="w-full rounded-[1.5rem] object-cover shadow-[0_30px_80px_-30px_rgba(74,93,79,0.35)] bg-cream"
                data-testid="tmi-hero-video"
              />
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* QUALIFIER — Is this for you? */}
      <section id="tmi-qualifier" className="py-20 md:py-28 bg-cream-soft border-b border-hairline">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <SectionReveal>
              <div className="bg-white border border-sage/30 rounded-2xl p-8 md:p-10 h-full">
                <div className="eyebrow mb-5 text-sage">This is for you if…</div>
                <ul className="space-y-4 text-ink leading-relaxed">
                  <li className="flex gap-3" data-testid="tmi-for-you-1">
                    <Check size={18} strokeWidth={2} className="text-sage flex-shrink-0 mt-1" />
                    <span>You're educated on menopause and past the basics</span>
                  </li>
                  <li className="flex gap-3" data-testid="tmi-for-you-2">
                    <Check size={18} strokeWidth={2} className="text-sage flex-shrink-0 mt-1" />
                    <span>You're tired of the noise around symptoms and want something deeper</span>
                  </li>
                  <li className="flex gap-3" data-testid="tmi-for-you-3">
                    <Check size={18} strokeWidth={2} className="text-sage flex-shrink-0 mt-1" />
                    <span>You sense there's more to this season than what everyone complains about</span>
                  </li>
                  <li className="flex gap-3" data-testid="tmi-for-you-4">
                    <Check size={18} strokeWidth={2} className="text-sage flex-shrink-0 mt-1" />
                    <span>You're ready to meet yourself differently — with awareness, not anxiety</span>
                  </li>
                </ul>
              </div>
            </SectionReveal>
            <SectionReveal delay={120}>
              <div className="bg-white border border-hairline rounded-2xl p-8 md:p-10 h-full">
                <div className="eyebrow mb-5 text-ink-soft">This is not for you if…</div>
                <ul className="space-y-4 text-ink-soft leading-relaxed">
                  <li className="flex gap-3" data-testid="tmi-not-for-you-1">
                    <span className="text-ink-soft/50 flex-shrink-0 mt-0.5">—</span>
                    <span>You're looking for medical advice or a hormone protocol</span>
                  </li>
                  <li className="flex gap-3" data-testid="tmi-not-for-you-2">
                    <span className="text-ink-soft/50 flex-shrink-0 mt-0.5">—</span>
                    <span>You want a symptom checklist or a diet plan</span>
                  </li>
                  <li className="flex gap-3" data-testid="tmi-not-for-you-3">
                    <span className="text-ink-soft/50 flex-shrink-0 mt-0.5">—</span>
                    <span>You're not ready to slow down and get honest with yourself</span>
                  </li>
                </ul>
              </div>
            </SectionReveal>
          </div>

          {!alreadyOwns && (
            <SectionReveal delay={200}>
              <div id="tmi-buy-section" className="mt-14 md:mt-16 text-center max-w-2xl mx-auto scroll-mt-24">
                <p className="font-serif-display text-2xl md:text-3xl text-ink leading-snug italic">
                  If you&apos;re still reading, you already know this is for you.
                </p>
                <div className="mt-10 flex flex-col items-center gap-3">
                  <button
                    onClick={() => handleBuy("menopause_invitation")}
                    disabled={loading}
                    data-testid="tmi-buy-btn"
                    className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all shadow-sm hover:shadow-md disabled:opacity-50"
                  >
                    {loading ? "Taking you to checkout…" : "Begin — $87"}
                    <ArrowRight size={16} strokeWidth={1.8} />
                  </button>
                  <span className="text-sm text-ink-soft">One-time payment · Lifetime access</span>
                  <Link
                    to="/the-menopause-invitation-live"
                    className="mt-4 text-sm text-ink-soft hover:text-sage transition underline underline-offset-4"
                    data-testid="tmi-digital-to-live"
                  >
                    Want live support and a group experience? Explore The Menopause Invitation — Live →
                  </Link>
                </div>
              </div>
            </SectionReveal>
          )}
        </div>
      </section>

      {/* PULL-QUOTE */}
      <section className="bg-peach/70 py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <SectionReveal>
            <div className="font-serif-display text-3xl md:text-5xl lg:text-[3.4rem] text-sage leading-[1.15] italic">
              "Your body isn't breaking down. It's moving through a recalibration —
              one that, if met consciously, can bring clarity, truth, and a different kind of
              awareness to the surface."
            </div>
            <div className="mt-8 eyebrow text-ink">— Pip</div>
          </SectionReveal>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-20 md:py-28 bg-cream-soft border-b border-hairline">
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow mb-4 text-sage">What's Included</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-ink leading-tight max-w-3xl">
              A grounded, considered space — built for the way you actually want to move through this.
            </h2>
          </SectionReveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {INCLUDED.map((item, i) => (
              <SectionReveal key={i} delay={i * 80}>
                <div className="bg-white border border-hairline rounded-2xl p-8 h-full">
                  <item.icon size={24} strokeWidth={1.4} className="text-sage mb-4" />
                  <p className="text-sm text-ink leading-relaxed">{item.label}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* SIX MODULES */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <SectionReveal>
            <div className="eyebrow mb-4 text-sage">The Six Modules</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-ink leading-tight max-w-3xl">
              From narrative to nervous system. From assumption to awareness.
            </h2>
            <p className="mt-6 text-ink-soft max-w-2xl leading-relaxed">
              Each module shifts the lens — gently, deliberately. There are no protocols here. There
              are questions designed to create space for your own clarity to emerge.
            </p>
          </SectionReveal>

          <div className="mt-14 space-y-4">
            {MODULES.map((m, i) => (
              <SectionReveal key={i} delay={i * 50}>
                <div className="group bg-white border border-hairline rounded-2xl p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 hover:border-sage hover:shadow-[0_20px_40px_-20px_rgba(74,93,79,0.12)] transition-all">
                  <div className="font-serif-display text-5xl md:text-6xl text-gold/80 font-light tabular-nums flex-shrink-0 w-20">
                    {m.n}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif-display text-2xl md:text-3xl text-ink leading-tight">{m.title}</h3>
                    <p className="mt-3 text-ink-soft leading-relaxed">{m.blurb}</p>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECOND PULL */}
      <section className="py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <SectionReveal>
            <Sparkles size={28} strokeWidth={1.3} className="mx-auto text-gold mb-8" />
            <div className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.15]">
              "You are not losing yourself. You are standing at a point where you have the
              opportunity to <em className="italic text-sage">meet yourself</em> more honestly than before."
            </div>
            <div className="mt-8 eyebrow text-ink-soft">— From Module Three</div>
          </SectionReveal>
        </div>
      </section>

      {/* PRICE */}
      <section className="py-24 md:py-32 bg-sage text-cream">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <SectionReveal>
            <div className="eyebrow mb-6 text-gold">Your Investment</div>
            <div className="font-serif-display text-7xl md:text-8xl lg:text-9xl">$87</div>
            <p className="mt-4 text-cream/70 text-lg">AUD · One-time · Lifetime access</p>
            <p className="mt-8 max-w-xl mx-auto text-cream/85 leading-relaxed text-lg">
              An honest, considered companion for one of the most significant transitions of your
              life. The kind of work I wish more people had access to — without the noise.
            </p>
            {alreadyOwns ? (
              <Link
                to="/dashboard/menopause"
                className="mt-10 inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-ink rounded-full px-10 py-5 font-medium tracking-wide transition-all"
                data-testid="tmi-price-cta-owned"
              >
                Enter your space
                <ArrowRight size={16} strokeWidth={1.8} />
              </Link>
            ) : (
              <button
                onClick={handleBuy}
                disabled={loading}
                className="mt-10 inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-ink rounded-full px-10 py-5 font-medium tracking-wide transition-all disabled:opacity-50"
                data-testid="tmi-price-cta"
              >
                {loading ? "Redirecting…" : "Begin — $87"}
                <ArrowRight size={16} strokeWidth={1.8} />
              </button>
            )}
            <p className="mt-6 text-xs text-cream/60 flex items-center justify-center gap-2">
              <ShieldCheck size={12} strokeWidth={1.5} />
              14-day peace-of-mind guarantee · Secure checkout by Stripe
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-cream-soft border-t border-hairline">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <SectionReveal>
            <div className="eyebrow mb-4 text-sage">Questions</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-ink leading-tight">
              You might be wondering…
            </h2>
          </SectionReveal>
          <div className="mt-14 space-y-4">
            {FAQ.map((f, i) => (
              <SectionReveal key={i} delay={i * 60}>
                <details className="group bg-white border border-hairline rounded-2xl p-8 transition-all" data-testid={`tmi-faq-${i}`}>
                  <summary className="cursor-pointer flex items-start justify-between gap-6 list-none">
                    <span className="font-serif-display text-lg md:text-xl text-ink">{f.q}</span>
                    <span className="mt-1 text-sage text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-5 text-ink-soft leading-relaxed">{f.a}</p>
                </details>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <SectionReveal>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-ink leading-[1.08]">
              When you're ready — there's a different way to meet this.
            </h2>
            {!alreadyOwns && (
              <button
                onClick={handleBuy}
                disabled={loading}
                className="mt-12 inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-10 py-5 font-medium tracking-wide transition-all disabled:opacity-50"
                data-testid="tmi-final-cta"
              >
                {loading ? "Redirecting…" : "Begin The Menopause Invitation — $87"}
                <ArrowRight size={16} strokeWidth={1.8} />
              </button>
            )}
          </SectionReveal>
        </div>
      </section>
      <StickyCTA
        label="Begin — $87"
        href="#tmi-buy-section"
        testId="sticky-cta-tmi"
      />
    </div>
  );
}
